import { UserInfo, UserRole } from '../user';

export enum OrganizationType {
    careHome = 'care_home',
    nursingHome = 'nursing_home',
}

export interface OrganizationGroup {
    id: string;
    name: string;
    parentId?: string;
    levelId?: string;
}

export interface OrganizationHierarchyLevel {
    id: string;
    name: string;
    parentId?: string;
}

export enum OrganizationRolePermission {
    readResidents = 'residents:read',
    writeResidents = 'residents:write',
    readEmployees = 'employees:read',
    writeEmployees = 'employees:write',
    sendMessagesToAll = 'messages:send_to_all',
}

export interface OrganizationRole {
    id: string;
    name: string;
    groupIds: string[];
    permissions: OrganizationRolePermission[];
}

export interface OrganizationDocument {
    id: string;
    blobName: string;
    originalName: string;
    displayName?: string;
    mimeType: string;
    size?: number;
    uploadedById: string;
    createdDate: string;
}

export interface OrganizationDocumentViewCount {
    documentId: string;
    residentCount: number;
    relativeCount: number;
    totalCount: number;
}

interface OrganizationProfile {
    firstName: string;
    lastName: string;
    jobTitle?: string;
    avatar?: string;
}

export enum OrganizationModuleType {
    calendar = 'calendar',
    infoScreen = 'infoScreen',
    analytics = 'analytics',
    appContent = 'appContent',
}

export enum OrganizationStructureVersion {
    legacy = 1,
    separated = 2,
}

export const ORGANIZATION_MODULE_DEFAULTS: Record<
    OrganizationModuleType,
    { employeeReadAccess: boolean }
> = {
    [OrganizationModuleType.calendar]: {
        employeeReadAccess: true,
    },
    [OrganizationModuleType.infoScreen]: {
        employeeReadAccess: false,
    },
    // Employee activity figures are shared only with explicit module members.
    [OrganizationModuleType.analytics]: {
        employeeReadAccess: false,
    },
    [OrganizationModuleType.appContent]: {
        employeeReadAccess: true,
    },
};

export interface OrganizationModuleHistoryEntry {
    enabled: boolean;
    changedById: string;
    date: string;
}

export interface OrganizationModule {
    type: OrganizationModuleType;
    enabled: boolean;
    history: OrganizationModuleHistoryEntry[];
    memberIds?: string[];
    memberLimit?: number;
    employeeReadAccess?: boolean;
}

export function isOrganizationModuleMember(
    organization: Pick<Organization, 'modules'> | undefined,
    type: OrganizationModuleType,
    userId: string | undefined,
): boolean {
    if (!userId) return false;

    const module = organization?.modules?.find(entry => entry.type === type);

    return Boolean(module?.memberIds?.includes(userId));
}

export interface Organization extends UserInfo {
    email: string;
    phoneNumber?: string;
    department?: string;
    organizationName: string;
    organizationDisplayName: string;
    structureVersion?: OrganizationStructureVersion;
    legacyAdministratorRetiredAt?: string;
    legacyAdministratorReplacementId?: string;
    avatarUrl?: string;
    superuserAvatar?: string;
    superuserAvatarUrl?: string;
    disableLogoDesignAdjustments?: boolean;
    infoScreenEnabled?: boolean;
    profile: OrganizationProfile;
    licence: number;
    role: UserRole.organization | UserRole.organizationB2C;
    type?: OrganizationType;
    roles?: OrganizationRole[];
    groups?: OrganizationGroup[];
    hierarchy?: OrganizationHierarchyLevel[];
    documentsFolderName?: string;
    documents?: OrganizationDocument[];
    modules?: OrganizationModule[];
}

export function isOrganizationModuleEnabled(
    organization:
        | Pick<Organization, 'modules' | 'infoScreenEnabled'>
        | undefined,
    type: OrganizationModuleType,
): boolean {
    const module = organization?.modules?.find(entry => entry.type === type);

    if (module) {
        return module.enabled === true;
    }

    // Documents were published before App content became a module, so it
    // stays enabled until a module entry exists (server ADR 0011).
    if (type === OrganizationModuleType.appContent) {
        return true;
    }

    return Boolean(
        type === OrganizationModuleType.infoScreen &&
            organization?.infoScreenEnabled === true,
    );
}

export function isOrganizationModuleEmployeeReadAccessEnabled(
    organization: Pick<Organization, 'modules'> | undefined,
    type: OrganizationModuleType,
): boolean {
    const module = organization?.modules?.find(entry => entry.type === type);

    return (
        module?.employeeReadAccess ??
        ORGANIZATION_MODULE_DEFAULTS[type].employeeReadAccess
    );
}

export function canEmployeeReadOrganizationModule(
    organization:
        | Pick<Organization, 'modules' | 'infoScreenEnabled'>
        | undefined,
    type: OrganizationModuleType,
    userId: string | undefined,
): boolean {
    return (
        isOrganizationModuleEnabled(organization, type) &&
        (isOrganizationModuleMember(organization, type, userId) ||
            isOrganizationModuleEmployeeReadAccessEnabled(organization, type))
    );
}

/** Only explicit members manage App content, including organization
 * administrators. */
export function canManageAppContent(
    organization:
        | Pick<Organization, 'modules' | 'infoScreenEnabled'>
        | undefined,
    userId: string | undefined,
): boolean {
    return (
        isOrganizationModuleEnabled(
            organization,
            OrganizationModuleType.appContent,
        ) &&
        isOrganizationModuleMember(
            organization,
            OrganizationModuleType.appContent,
            userId,
        )
    );
}

export interface CreateOrganizationPayload {
    email: string;
    phoneNumber?: string;
    department?: string;
    organizationName: string;
    organizationDisplayName: string;
    disableLogoDesignAdjustments?: boolean;
    infoScreenEnabled?: boolean;
    type?: OrganizationType;
    profile: OrganizationProfile;
    licence: number;
    initialPassword?: string;
    groups?: OrganizationGroup[];
    roles?: OrganizationRole[];
    hierarchy?: OrganizationHierarchyLevel[];
}

export interface LicenceEntity {
    total: number;
    active: number;
}
